import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Divider,
  Anchor,
  Alert,
  Group,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconMailCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { GoogleLogin, GoogleOAuthProvider, type CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth';
import { Logo } from '../../components/common';
import { config } from '../../config';

interface FormValues {
  emailOrUsername: string;
  password: string;
}

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [emailNotVerified, setEmailNotVerified] = useState(false);

  const rawFrom = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
  // Prevent open redirect: only allow relative paths on the same origin
  const from = rawFrom.startsWith('/') && !rawFrom.startsWith('//') ? rawFrom : '/';

  const form = useForm<FormValues>({
    initialValues: {
      emailOrUsername: '',
      password: '',
    },
    validate: {
      emailOrUsername: (value) => (value.trim().length > 0 ? null : 'Email or username is required'),
      password: (value) => (value.length >= 6 ? null : 'Password must be at least 6 characters'),
    },
  });

  const loginMutation = useMutation({
    mutationFn: (values: FormValues) => login(values),
    onSuccess: () => navigate(from, { replace: true }),
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setEmailNotVerified(true);
        setError('');
      } else if (status === 401 || status === 400) {
        setEmailNotVerified(false);
        setError('Invalid email/username or password.');
      } else {
        setEmailNotVerified(false);
        setError('Something went wrong. Please try again.');
      }
    },
  });

  const resendMutation = useMutation({
    mutationFn: () => authService.resendVerificationEmail(form.values.emailOrUsername),
    onSuccess: () => {
      notifications.show({ title: 'Email sent', message: 'Check your inbox for the verification link.', color: 'green' });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to resend verification email.', color: 'red' });
    },
  });

  const handleSubmit = (values: FormValues) => {
    setError('');
    setEmailNotVerified(false);
    loginMutation.mutate(values);
  };

  return (
    <Container size={420} my={40}>
      <Stack align="center" mb="lg">
        <Logo size="lg" />
        <Title order={2} ta="center">
          Welcome back
        </Title>
        <Text c="dimmed" size="sm" ta="center">
          Don't have an account?{' '}
          <Anchor component={Link} to="/register" size="sm">
            Sign up
          </Anchor>
        </Text>
      </Stack>

      <Paper withBorder shadow="md" p={30} radius="md">
        {emailNotVerified && (
          <Alert icon={<IconMailCheck size={16} />} color="yellow" mb="md" title="Email not verified">
            <Text size="sm">Please verify your email before logging in.</Text>
            <Button
              variant="subtle"
              size="compact-sm"
              mt="xs"
              loading={resendMutation.isPending}
              onClick={() => resendMutation.mutate()}
            >
              Resend verification email
            </Button>
          </Alert>
        )}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
            {error}
          </Alert>
        )}

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Email or Username"
              placeholder="Email or username"
              required
              {...form.getInputProps('emailOrUsername')}
            />

            <PasswordInput
              label="Password"
              placeholder="Your password"
              required
              {...form.getInputProps('password')}
            />

            <Group justify="flex-end">
              <Anchor component={Link} to="/forgot-password" size="sm">
                Forgot password?
              </Anchor>
            </Group>

            <Button type="submit" fullWidth loading={loginMutation.isPending}>
              Log in
            </Button>
          </Stack>
        </form>

        <Divider label="Or continue with" labelPosition="center" my="lg" />

        {config.googleClientId !== 'not-configured' && (
          <Group justify="center">
            <GoogleOAuthProvider clientId={config.googleClientId}>
              <GoogleLogin
                onSuccess={async (response: CredentialResponse) => {
                  if (response.credential) {
                    try {
                      setError('');
                      await loginWithGoogle(response.credential);
                      navigate(from, { replace: true });
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Google login failed. Please try again.');
                    }
                  }
                }}
                onError={() => {
                  setError('Google login failed. Please try again.');
                }}
                width="350"
              />
            </GoogleOAuthProvider>
          </Group>
        )}
      </Paper>
    </Container>
  );
}
